/**
 * Settings API service — categories, manufacturers, sites, customers.
 */
import api from '@/lib/api';
import { MOCK_CATEGORIES, MOCK_MANUFACTURERS, MOCK_SITES, MOCK_CUSTOMERS } from '@/data/mockData';
import type { Category, Manufacturer, Site, Customer } from '@/types/models';

const USE_MOCK = !import.meta.env.VITE_API_URL || import.meta.env.VITE_USE_MOCK === 'true';

function delay<T>(val: T, ms = 250): Promise<T> {
  return new Promise((r) => setTimeout(() => r(val), ms));
}

function unwrapData<T>(payload: unknown): T | undefined {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data?: T }).data;
  }

  return payload as T;
}

function unwrapCollection<T>(payload: unknown): T[] {
  const unwrapped = unwrapData<unknown>(payload);

  if (Array.isArray(unwrapped)) {
    return unwrapped as T[];
  }

  if (unwrapped && typeof unwrapped === 'object') {
    const firstArray = Object.values(unwrapped as Record<string, unknown>).find(Array.isArray);
    if (Array.isArray(firstArray)) {
      return firstArray as T[];
    }
  }

  return [];
}

function unwrapResource<T>(payload: unknown): T {
  const unwrapped = unwrapData<unknown>(payload);

  if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
    const values = Object.values(unwrapped as Record<string, unknown>);
    const [firstValue] = values;

    if (values.length === 1 && firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
      return firstValue as T;
    }
  }

  return (unwrapped ?? payload) as T;
}

// ── Categories ─────────────────────────────────────────

export async function fetchCategories(): Promise<Category[]> {
  if (USE_MOCK) return delay([...MOCK_CATEGORIES]);
  const res = await api.get('/api/categories');
  return unwrapCollection<Category>(res.data);
}

export async function createCategory(name: string): Promise<Category> {
  if (USE_MOCK) {
    const cat: Category = { id: name.toLowerCase().replace(/\s+/g, '_'), name, is_active: true };
    MOCK_CATEGORIES.push(cat);
    return delay(cat);
  }
  const res = await api.post('/api/categories', { name });
  return unwrapResource<Category>(res.data);
}

export async function updateCategory(id: string, updates: Partial<Category>): Promise<Category> {
  if (USE_MOCK) {
    const idx = MOCK_CATEGORIES.findIndex((c) => c.id === id);
    if (idx >= 0) {
      MOCK_CATEGORIES[idx] = { ...MOCK_CATEGORIES[idx], ...updates };
      return delay(MOCK_CATEGORIES[idx]);
    }
    throw new Error('Category not found');
  }
  const res = await api.patch(`/api/categories/${id}`, updates);
  return unwrapResource<Category>(res.data);
}

export async function deleteCategory(id: string): Promise<void> {
  if (USE_MOCK) {
    const idx = MOCK_CATEGORIES.findIndex((c) => c.id === id);
    if (idx >= 0) MOCK_CATEGORIES.splice(idx, 1);
    return delay(undefined);
  }
  await api.delete(`/api/categories/${id}`);
}

// ── Manufacturers ──────────────────────────────────────

export async function fetchManufacturers(): Promise<Manufacturer[]> {
  if (USE_MOCK) return delay([...MOCK_MANUFACTURERS]);
  const res = await api.get('/api/manufacturers');
  return unwrapCollection<Manufacturer>(res.data);
}

export async function createManufacturer(name: string): Promise<Manufacturer> {
  if (USE_MOCK) {
    const mfg: Manufacturer = { id: name.toLowerCase().replace(/\s+/g, '_'), name, is_active: true };
    MOCK_MANUFACTURERS.push(mfg);
    return delay(mfg);
  }
  const res = await api.post('/api/manufacturers', { name });
  return unwrapResource<Manufacturer>(res.data);
}

export async function updateManufacturer(id: string, updates: Partial<Manufacturer>): Promise<Manufacturer> {
  if (USE_MOCK) {
    const idx = MOCK_MANUFACTURERS.findIndex((m) => m.id === id);
    if (idx >= 0) {
      MOCK_MANUFACTURERS[idx] = { ...MOCK_MANUFACTURERS[idx], ...updates };
      return delay(MOCK_MANUFACTURERS[idx]);
    }
    throw new Error('Manufacturer not found');
  }
  const res = await api.patch(`/api/manufacturers/${id}`, updates);
  return unwrapResource<Manufacturer>(res.data);
}

export async function deleteManufacturer(id: string): Promise<void> {
  if (USE_MOCK) {
    const idx = MOCK_MANUFACTURERS.findIndex((m) => m.id === id);
    if (idx >= 0) MOCK_MANUFACTURERS.splice(idx, 1);
    return delay(undefined);
  }
  await api.delete(`/api/manufacturers/${id}`);
}

// ── Sites ──────────────────────────────────────────────

export async function fetchSites(): Promise<Site[]> {
  if (USE_MOCK) return delay([...MOCK_SITES]);
  const res = await api.get('/api/sites');
  return unwrapCollection<Site>(res.data);
}

// ── Customers ──────────────────────────────────────────

export async function fetchCustomers(): Promise<Customer[]> {
  if (USE_MOCK) return delay([...MOCK_CUSTOMERS]);
  const res = await api.get('/api/customers');
  return unwrapCollection<Customer>(res.data);
}
